import * as React from "react";
import { useContext } from "react";
import styled, { css } from "styled-components";
import ChartLegend from "components/common/ChartLegend";
import Value from "views/components/Value";
import ChartColorContext from "views/components/visualizations/ChartColorContext";
import FieldType from "views/logic/fieldtypes/FieldType";
import { colors as defaultColors } from "views/components/visualizations/Colors";
import { EVENT_COLOR, eventsDisplayName } from "views/logic/searchtypes/events/EventHandler";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import { keySeparator, humanSeparator } from "views/Constants";
import useMapKeys from "views/components/visualizations/useMapKeys";
import { useIsInteractiveMode } from "views/components/contexts/InteractiveContext";
import WidgetRenderingContext from "views/components/widgets/WidgetRenderingContext";
const FixedContainer = styled.div`
  display: grid;
  grid-template: 4fr auto / 1fr;
  grid-template-areas: '.' '.';
  height: 100%;
`;
const VariableContainer = styled.div(
  ({ $height, $width }) => css`
    display: grid;
    width: ${$width}px;
    grid-template-columns: ${$width}px;
    grid-template-rows: ${$height}px auto;
    gap: 0;
    justify-content: center;
  `
);
const defaultLabelMapper = (data) => data.map(({ name, originalName }) => originalName != null ? originalName : name);
const stringLenSort = (s1, s2) => {
  if (s1.length < s2.length) {
    return -1;
  }
  if (s1.length === s2.length) {
    return 0;
  }
  return 1;
};
const columnPivotsToFields = (config) => {
  var _a, _b;
  return (_b = (_a = config == null ? void 0 : config.columnPivots) == null ? void 0 : _a.flatMap((pivot) => pivot.fields)) != null ? _b : [];
};
const labelsWithFields = (value, labelFields, fieldTypes) => value.split(keySeparator).map((label, idx) => {
  var _a, _b, _c;
  const field = labelFields[idx];
  const type = (_c = (_b = (_a = fieldTypes == null ? void 0 : fieldTypes.currentQuery) == null ? void 0 : _a.find((fieldType) => fieldType.name === field)) == null ? void 0 : _b.type) != null ? _c : FieldType.Unknown;
  return { label, field, type };
});
const PlotLegend = ({
  children,
  config,
  chartData,
  labelMapper = defaultLabelMapper,
  labelFields = columnPivotsToFields,
  neverHide = false,
  height,
  width
}) => {
  const { columnPivots, series } = config;
  const { focusedWidget } = useContext(WidgetFocusContext);
  const interactive = useIsInteractiveMode();
  const fieldTypes = useContext(FieldTypesContext);
  const { limitHeight } = useContext(WidgetRenderingContext);
  const { colors, setColor } = useContext(ChartColorContext);
  const mapKeys = useMapKeys();
  const labels = labelMapper(chartData);
  const Container = limitHeight ? FixedContainer : VariableContainer;
  if (!neverHide && !(focusedWidget == null ? void 0 : focusedWidget.editing) && series.length <= 1 && columnPivots.length <= 0) {
    return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, children);
  }
  const fields = labelFields(config);
  const items = (interactive ? labels.sort(stringLenSort) : labels).map((value) => {
    const labelsWithField = labelsWithFields(value, fields, fieldTypes);
    return {
      key: value,
      color: colors.get(value, value === eventsDisplayName ? EVENT_COLOR : void 0),
      title: labelsWithField.map(({ label, field }) => mapKeys(label, field)).join(humanSeparator),
      label: labelsWithField.map(
        ({ label, field, type }) => field ? /* @__PURE__ */ React.createElement(Value, { key: `${field}:${label}`, type, value: label, field }) : label
      )
    };
  });
  return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, children, /* @__PURE__ */ React.createElement(
    ChartLegend,
    {
      items,
      layout: interactive ? "grid" : "wrap",
      colorPicker: interactive ? { colors: defaultColors, onChange: setColor } : void 0
    }
  ));
};
export default PlotLegend;
